import React from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardVM from "../viewmodel/DashboardVM";

// Colours for the state bars (checked for colour-blind safety)
const STATE_COLORS = {
  assigned: "#2a78d6", // blue
  maintenance: "#eb6834", // orange
  available: "#1baf7a", // aqua
  other: "#b4b2ab", // grey
};
// Same order as the table columns
const STATES = [
  { key: "assigned", label: "Assigned" },
  { key: "available", label: "Available" },
  { key: "maintenance", label: "Maintenance" },
  { key: "other", label: "Other" },
];

const headCell = {
  fontWeight: "bold",
  backgroundColor: "#052660",
  color: "#fff",
  fontSize: "0.85rem",
  whiteSpace: "nowrap",
};

// "2026-11-15" -> "15/11/2026"
const toDisplayDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const Tile = ({ label, value, hint, color = "#052660" }) => (
  <Box
    sx={{
      flex: "1 1 0",
      minWidth: 130,
      boxSizing: "border-box",
      border: "1px solid #e3e6ea",
      borderRadius: "8px",
      p: "12px 14px",
      backgroundColor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: "0.8rem", color: "#555" }}>{label}</Typography>
    <Typography sx={{ fontSize: "1.8rem", fontWeight: 700, color, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {hint && <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>{hint}</Typography>}
  </Box>
);

// One stacked bar per category: Assigned | Available | Maintenance | Other
const StateBar = ({ row }) => {
  const total = row.total || 1;
  return (
    <Box sx={{ display: "flex", gap: "2px", height: 12, minWidth: 160 }}>
      {STATES.filter((s) => row[s.key] > 0).map((s) => (
        <Tooltip key={s.key} title={`${s.label}: ${row[s.key]}`} arrow>
          <Box
            sx={{
              width: `${(row[s.key] / total) * 100}%`,
              backgroundColor: STATE_COLORS[s.key],
              borderRadius: "2px",
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

const DashboardPanel = ({ active }) => {
  const { data, loading, days, handleDaysChange, reload } = DashboardVM({ active });

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!data) {
    return <Typography sx={{ color: "#666", p: 2 }}>No dashboard data.</Typography>;
  }

  const { totals, by_category: categories, warranty } = data;

  return (
    <Grid container sx={{ width: "100%", pb: "2.5em" }}>
      {/* Header */}
      <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, color: "#052660", flex: 1 }}>
          Assets in use (retired not counted)
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={reload} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Grid>

      {/* 1. Count boxes */}
      <Grid item xs={12} sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        <Tile label="Total" value={totals.total} />
        <Tile label="Assigned" value={totals.assigned} hint="someone has it" />
        <Tile label="Available" value={totals.available} hint="free to give out" />
        <Tile label="Maintenance" value={totals.maintenance} />
        <Tile label="Retired" value={totals.retired} hint="lost / sold / e-waste" />
        <Tile
          label="Not confirmed"
          value={totals.ack_pending}
          hint="employee hasn't confirmed receipt"
        />
      </Grid>

      {/* 2. By category */}
      <Grid item xs={12} sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "#052660", flex: 1 }}>
            By category
          </Typography>
          {/* Legend */}
          {STATES.map((s) => (
            <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: STATE_COLORS[s.key] }}
              />
              <Typography sx={{ fontSize: "0.8rem", color: "#555" }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {["Category", "Total", "Assigned", "Available", "Maintenance", "Other", ""].map(
                  (h, i) => (
                    <TableCell key={i} sx={{ ...headCell, textAlign: i > 0 && i < 6 ? "right" : "left" }}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", color: "#666", py: 3 }}>
                    No assets yet
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((c) => (
                  <TableRow key={c.category} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{c.category}</TableCell>
                    <TableCell sx={{ textAlign: "right", fontWeight: 600 }}>{c.total}</TableCell>
                    <TableCell sx={{ textAlign: "right" }}>{c.assigned}</TableCell>
                    <TableCell sx={{ textAlign: "right" }}>{c.available}</TableCell>
                    <TableCell sx={{ textAlign: "right" }}>{c.maintenance}</TableCell>
                    <TableCell sx={{ textAlign: "right" }}>{c.other}</TableCell>
                    <TableCell sx={{ width: "30%" }}>
                      <StateBar row={c} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.5 }}>
          "Assigned" = someone has it now. "Other" = any other status (e.g. Obsolete, or a
          status that doesn't match the history).
        </Typography>
      </Grid>

      {/* 3. Warranty expiring soon */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "#052660", flex: 1 }}>
            Warranty expiring in the next {days} days ({warranty.expiring.length})
          </Typography>
          <TextField
            select
            size="small"
            label="Show"
            value={days}
            onChange={(e) => handleDaysChange(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            {[30, 60, 90].map((d) => (
              <MenuItem key={d} value={d}>
                Next {d} days
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {["Asset ID", "Category", "Brand", "Employee", "Warranty ends", "Days left"].map(
                  (h) => (
                    <TableCell key={h} sx={headCell}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {warranty.expiring.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", color: "#666", py: 3 }}>
                    No warranty ends in the next {days} days
                  </TableCell>
                </TableRow>
              ) : (
                warranty.expiring.map((w) => (
                  <TableRow key={w.asset_id} hover>
                    <TableCell>
                      <a
                        href={`/asset?id=${w.asset_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#052660" }}
                      >
                        {w.asset_id}
                      </a>
                    </TableCell>
                    <TableCell>{w.category || "-"}</TableCell>
                    <TableCell>{w.brand || "-"}</TableCell>
                    <TableCell>{w.employee_name || "Not assigned"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{toDisplayDate(w.warranty_date)}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          backgroundColor: w.days_left <= 7 ? "#ffebee" : "#fff3e0",
                          color: w.days_left <= 7 ? "#c62828" : "#e65100",
                          fontSize: "0.8em",
                          padding: "2px 10px",
                          borderRadius: "10px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {w.days_left === 0 ? "Today" : `${w.days_left} day(s)`}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: "0.8rem", color: "#666", mt: 1 }}>
          Already expired: <b>{warranty.expired_count}</b> · No warranty date:{" "}
          <b>{warranty.no_warranty_date_count}</b>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default DashboardPanel;